import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuItemVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTooltipPlacement,
} from "@/mds";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { DateTime } from "luxon";
import { uuidModule } from "@/modules/uuid";
import { AppStore } from "@/store";
import styled from "@emotion/styled";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { EventContext } from "@/domains/metrics/context";

function getEventDate(event: SpaceAccountCalendarEventObservable): DateTime | null {
  if (!event.startTime) return null;
  const date = DateTime.fromISO(event.startTime);
  return date.isValid ? date : null;
}

function getSectionForEvent(event: SpaceAccountCalendarEventObservable): string {
  const eventDate = getEventDate(event);
  if (!eventDate) return "Other";
  const today = DateTime.local();
  if (eventDate.hasSame(today, "day")) return "Today";
  if (eventDate.hasSame(today.plus({ days: 1 }), "day")) return "Tomorrow";
  return eventDate.weekdayLong ?? "Other";
}

export function getCalendarEventsMenuContentList({
  calendarEvents,
  createNoteUsingCalendarEventId,
  navigateToNote,
  activeNoteId,
  context,
}: {
  calendarEvents: SpaceAccountCalendarEventObservable[];
  createNoteUsingCalendarEventId: (payload: {
    noteId: string;
    calendarEventId: string;
  }) => Promise<void>;
  navigateToNote: ({ noteId, openInSameTab }: { noteId: string; openInSameTab: boolean }) => void;
  activeNoteId?: string;
  store: AppStore;
  context: EventContext;
}): MDSMenuContentList {
  const result: MDSMenuContentList = [];
  if (calendarEvents.length === 0) return result;

  // Sort events by startTime ascending
  const sortedEvents = [...calendarEvents].sort((a, b) => {
    if (!a.startTime && !b.startTime) return 0;
    if (!a.startTime) return 1;
    if (!b.startTime) return -1;
    return a.startTime.localeCompare(b.startTime);
  });

  let lastSection: string | null = null;

  for (const event of sortedEvents) {
    const currentSection = getSectionForEvent(event);

    // Add header when transitioning to a new section
    if (currentSection !== lastSection) {
      if (currentSection === "today") {
        result.push({
          id: "header-today",
          kind: MDSMenuContentItemKind.Header,
          label: "Today",
        });
      } else if (currentSection === "tomorrow") {
        result.push({
          id: "header-tomorrow",
          kind: MDSMenuContentItemKind.Header,
          label: "Tomorrow",
        });
      } else {
        result.push({
          id: `header-${currentSection}`,
          kind: MDSMenuContentItemKind.Header,
          label: currentSection,
        });
      }
      lastSection = currentSection;
    }

    const label = `${event.primaryLabel} • ${event.startTimeLabel}`;
    const subtitle = (
      <SubtitleWithIcon>
        {event.calendarEmailAddress && (
          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            {event.calendarEmailAddress}
          </MDSText>
        )}
        <MDSIcon
          kind={MDSIconKind.Dot}
          size={10}
          overrideColor={event.associatedSpaceAccountCalendar?.color}
          color={MDSIconColor.Secondary}
        />
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
          {event.calendarName}
        </MDSText>
      </SubtitleWithIcon>
    );
    const hasExistingNote = event.associatedNote;

    result.push({
      id: event.id,
      kind: MDSMenuContentItemKind.Item,
      variant: MDSMenuItemVariant.TwoLine,
      label,
      subtitle,
      icon: hasExistingNote ? MDSIconKind.Document : MDSIconKind.CalendarEvent,
      onClick: async () => {
        if (!hasExistingNote) {
          const noteId = activeNoteId || uuidModule.generate();
          await createNoteUsingCalendarEventId({ noteId, calendarEventId: event.id });
          trackEvent(TrackedEvent.NoteCreate, {
            context,
            connected_calendar_event_id: event.associatedConnectedCalendarEventId,
          });
          navigateToNote({ noteId, openInSameTab: !!activeNoteId });
        } else {
          navigateToNote({ noteId: hasExistingNote.id, openInSameTab: false });
        }
      },
      tooltipConfig: {
        label: hasExistingNote ? "Open note for this event" : "Start note for this event",
        placement: MDSTooltipPlacement.Top,
      },
      rightAddon: hasExistingNote ? () => <MDSIcon kind={MDSIconKind.ArrowUpRight} /> : undefined,
    });
  }

  return result;
}

const SubtitleWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
`;
